/**
 * Requests Distribution Pie Chart Component
 * Shows distribution of cache hits, misses, and errors
 */

import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';

export interface RequestsChartProps {
  hits: number;
  misses: number;
  errors: number;
  className?: string;
}

interface SliceData {
  labelKey: 'hits' | 'misses' | 'errors';
  value: number;
  color: string;
}

export function RequestsChart({
  hits,
  misses,
  errors,
  className = '',
}: RequestsChartProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.charts;
  const tCache = messages.monitoring.cache;
  const tDetails = messages.monitoring.cacheDetails;

  const total = hits + misses + errors;

  const slices: SliceData[] = [
    { labelKey: 'hits', value: hits, color: '#10B981' },      // green-500
    { labelKey: 'misses', value: misses, color: '#F59E0B' },  // yellow-500
    { labelKey: 'errors', value: errors, color: '#EF4444' },  // red-500
  ];

  // Get translated label
  const getLabel = (key: SliceData['labelKey']) => {
    if (key === 'errors') return tDetails.errors;
    return tCache[key];
  };

  // SVG pie chart parameters
  const size = 140;
  const center = size / 2;
  const radius = 50;

  // Calculate pie slices
  let cumulativeAngle = 0;
  const paths = slices
    .filter((s) => s.value > 0)
    .map((slice) => {
      const percentage = total > 0 ? slice.value / total : 0;
      const angle = percentage * 360;
      const startAngle = cumulativeAngle;
      const endAngle = cumulativeAngle + angle;
      cumulativeAngle = endAngle;

      // Convert angles to radians
      const startRad = ((startAngle - 90) * Math.PI) / 180;
      const endRad = ((endAngle - 90) * Math.PI) / 180;

      // Calculate arc points
      const x1 = center + radius * Math.cos(startRad);
      const y1 = center + radius * Math.sin(startRad);
      const x2 = center + radius * Math.cos(endRad);
      const y2 = center + radius * Math.sin(endRad);

      // Large arc flag
      const largeArc = angle > 180 ? 1 : 0;

      // Create arc path
      const path = `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

      return { ...slice, path };
    });

  return (
    <Card className={`${className}`}>
      <h3 className="text-sm font-medium text-gray-600 mb-4">{t.requestDistribution}</h3>
      <div className="flex items-center justify-center" style={{ minHeight: 160 }}>
        {total === 0 ? (
          <div className="text-gray-400 text-sm">{t.noData}</div>
        ) : (
          <div className="flex flex-col items-center">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              {paths.map((p, i) => (
                <path
                  key={i}
                  d={p.path}
                  fill={p.color}
                  className="transition-all duration-300"
                />
              ))}
            </svg>
            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-3">
              {slices.map((slice) => (
                <div key={slice.labelKey} className="flex items-center gap-1">
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: slice.color }}
                  />
                  <span className="text-xs text-gray-600">
                    {getLabel(slice.labelKey)} ({slice.value.toLocaleString()})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
